import { useEffect, useState } from "react";
import BaseModalV2 from "~/components/common/base-modal-v2/BaseModalV2";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import styles from "./RequestPaymentModal.module.scss";
import classNames from "classnames/bind";
import jobDetailsApi from "~/apis/jobDetails";
import { notify } from "~/utils/helper";

const cx = classNames.bind(styles);

interface RequestPaymentModalProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  onSuccess?: () => void;
}

const MAX_NOTE_LENGTH = 500;

const RequestPaymentModal = (props: RequestPaymentModalProps) => {
  const { open, onClose, jobId, onSuccess } = props;

  const [jobName, setJobName] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // "JOB NAME" = job title + job code, same as the Job Detail header ("Fix my roof - JOB-2025-644").
  // Payment requests are job-level, so this does not depend on which invoice (if any) opened the modal.
  useEffect(() => {
    const fetchJobName = async () => {
      try {
        const res = await jobDetailsApi.getJobDetails(jobId);
        const { name, jobId: jobCode } = res.data?.data ?? {};
        setJobName([name, jobCode].filter(Boolean).join(" - "));
      } catch (error) {
        // Silent fail; modal can still work without job name
        console.error(error);
      }
    };

    if (open && jobId) {
      setAmount("");
      setNote("");
      setJobName("");
      fetchJobName();
    }
  }, [open, jobId]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    const numericAmount = parseFloat(amount);
    if (!amount || Number.isNaN(numericAmount) || numericAmount <= 0) {
      notify("error", "Please enter a valid payment amount");
      return;
    }

    setIsSubmitting(true);
    try {
      await jobDetailsApi.createPaymentRequest(jobId, {
        amount: numericAmount,
        ...(note.trim() ? { messageToCustomer: note.trim() } : {}),
      });
      notify("success", "Payment request created successfully");
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error: any) {
      notify(
        "error",
        error?.response?.data?.message || "Failed to create payment request"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAmountChange = (value: string) => {
    const cleanedValue = value.replace(/[^0-9.]/g, "");
    const parts = cleanedValue.split(".");
    const normalizedValue =
      parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : cleanedValue;
    setAmount(normalizedValue);
  };

  const handleNoteChange = (value: string) => {
    if (value.length <= MAX_NOTE_LENGTH) {
      setNote(value);
    } else {
      setNote(value.slice(0, MAX_NOTE_LENGTH));
    }
  };

  const amountError =
    amount && (Number.isNaN(parseFloat(amount)) || parseFloat(amount) <= 0)
      ? "Amount must be greater than 0"
      : "";

  return (
    <BaseModalV2
      open={open}
      onClose={handleClose}
      title="Request Payment"
      width="90%"
      maxWidth="600px"
    >
      <div className={cx("request-payment-modal-container")}>
        <div className={cx("request-payment-modal-header")}>
          <div className={cx("job-name-label")}>JOB NAME</div>
          <div className={cx("job-name-value")}>
            {jobName || `Job #${jobId}`}
          </div>
        </div>

        <div className={cx("request-payment-modal-body")}>
          <div className={cx("field-group")}>
            <label className={cx("field-label")}>
              Payment amount
              <span className={cx("required")}>*</span>
            </label>
            <PrimaryInput
              name="paymentAmount"
              type="text"
              placeholder="$"
              value={amount}
              onChange={(e) => handleAmountChange(e.target.value)}
            />
            {amountError && (
              <div className={cx("error-message")}>{amountError}</div>
            )}
          </div>

          <div className={cx("field-group")}>
            <label className={cx("field-label")}>Note</label>
            <textarea
              className={cx("note-textarea")}
              placeholder="Message to customer: (i.e. estimate #)"
              value={note}
              onChange={(e) => handleNoteChange(e.target.value)}
            />
            <div className={cx("note-footer")}>
              {note.length} / {MAX_NOTE_LENGTH}
            </div>
          </div>
        </div>

        <div className={cx("request-payment-modal-footer")}>
          <PrimaryButton
            title="Back"
            onClick={handleClose}
            className={cx("back-button")}
            disabled={isSubmitting}
          />
          <PrimaryButton
            title="Next"
            onClick={handleSubmit}
            className={cx("next-button")}
            disabled={isSubmitting}
            loading={isSubmitting}
          />
        </div>
      </div>
    </BaseModalV2>
  );
};

export default RequestPaymentModal;

